import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Navigation item configuration
 */
export interface NavItemConfig {
  label: string
  href?: string
  icon?: LucideIcon
  badge?: number | string
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline'
  disabled?: boolean
  children?: NavItemConfig[]
  section?: string
}

interface NavItemProps {
  item: NavItemConfig
  isCollapsed: boolean
  isExpanded?: boolean
  onToggle?: () => void
  onNavigate?: () => void
  level?: number
}

/**
 * Single navigation item component
 * Per App-Shell.md Section 2 - Sidebar Navigation
 *
 * Clean, minimal active states
 */
export function NavItem({
  item,
  isCollapsed,
  isExpanded = false,
  onToggle,
  onNavigate,
  level = 0,
}: NavItemProps) {
  const location = useLocation()
  const hasChildren = item.children && item.children.length > 0
  const Icon = item.icon

  // Check if a href matches the current location (including query params)
  const isHrefActive = (href: string | undefined): boolean => {
    if (!href) return false

    // Parse href to check for query params
    const [hrefPath, hrefSearch] = href.split('?')
    const currentPath = location.pathname
    const currentSearch = location.search

    // If href has query params, check both path and search
    if (hrefSearch) {
      return currentPath === hrefPath && currentSearch === `?${hrefSearch}`
    }

    // For hrefs without query params, match exact path or path prefix (except root)
    if (hrefPath === '/') {
      return currentPath === '/'
    }

    /*
     * A query string must NOT, by itself, put the entry out.
     *
     * This used to be `if (currentSearch && currentPath === hrefPath) return false` — any query
     * at all un-highlighted the row. It was written so that `/quotations?task=…` could hand the
     * highlight to a child row that claimed exactly that query, and it hands it to nobody now:
     * **no nav href in the app carries a query any more**, so the rule only ever took the
     * highlight away. Every page that writes to the URL paid for it — open a drawer
     * (`?selected=…`), filter a list, switch a tab, and the sidebar went dark while you were
     * plainly still on that page. On the Calendar it was reproducible in three clicks: arrive
     * highlighted, switch to To-do (`?view=todo`) and it vanished, switch back
     * (`?view=calendar`) and it stayed gone, because coming back also writes a query.
     *
     * The original intent is kept, narrowed to the case it was for: step aside only when one of
     * THIS item's own children claims the exact path *and* search.
     */
    const childClaimsThisQuery =
      item.children?.some((child) => {
        const [childPath, childSearch] = (child.href ?? '').split('?')
        return Boolean(childSearch) && currentPath === childPath && currentSearch === `?${childSearch}`
      }) ?? false
    if (childClaimsThisQuery) return false

    return currentPath === hrefPath || currentPath.startsWith(`${hrefPath}/`)
  }

  // Check if this item is active
  const isActive = isHrefActive(item.href)

  // Check if any child is active
  const isChildActive = item.children?.some((child) => isHrefActive(child.href))

  // Collapsed state - icon only with tooltip
  // When collapsed, items with href should navigate directly (even if they have children)
  if (isCollapsed && level === 0) {
    const hasBadgeIndicator = item.badge || item.children?.some((c) => c.badge)

    // If item has an href, make it a link (regardless of children)
    if (item.href) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={item.href}
              onClick={onNavigate}
              className={cn(
                'group relative flex items-center justify-center rounded-md p-2 transition-colors cursor-pointer',
                'text-muted-foreground hover:bg-muted hover:text-foreground',
                (isActive || isChildActive) && 'bg-primary/10 text-primary',
                item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
              )}
            >
              {Icon && <Icon className="h-5 w-5" />}
              {hasBadgeIndicator && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500" />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <span>{item.label}</span>
            {typeof item.badge === 'number' && (
              <span className="ml-2 text-xs opacity-70">({item.badge})</span>
            )}
          </TooltipContent>
        </Tooltip>
      )
    }

    // No href - just show icon (for items like section headers)
    return (
      <span
        className={cn(
          'group relative flex items-center justify-center rounded-md p-2 transition-colors',
          'text-muted-foreground',
          item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
        )}
      >
        {Icon && <Icon className="h-5 w-5" />}
      </span>
    )
  }

  // Expanded state - full nav item
  if (item.href && !hasChildren) {
    return (
      <Link
        to={item.href}
        onClick={onNavigate}
        className={cn(
          'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-muted hover:text-foreground',
          isActive && 'bg-primary/10 text-primary',
          level > 0 && 'ml-6 pl-3',
          item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
        )}
      >
        {Icon && <Icon className="h-5 w-5 shrink-0" />}
        <span className="flex-1 truncate">{item.label}</span>
        {item.badge !== undefined && (
          <Badge
            variant={item.badgeVariant || 'secondary'}
            className={cn(
              'ml-auto text-xs h-5 min-w-[20px] justify-center',
              typeof item.badge === 'number' && 'bg-red-500 text-white hover:bg-red-500'
            )}
          >
            {item.badge}
          </Badge>
        )}
      </Link>
    )
  }

  // Expandable section with children
  if (hasChildren) {
    return (
      <div>
        <button
          onClick={onToggle}
          disabled={item.disabled}
          className={cn(
            'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'text-muted-foreground hover:bg-muted hover:text-foreground',
            isChildActive && 'text-foreground',
            item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
          )}
        >
          {Icon && <Icon className="h-5 w-5 shrink-0" />}
          <span className="flex-1 truncate text-left">{item.label}</span>
          {item.badge !== undefined && (
            <Badge
              variant={item.badgeVariant || 'secondary'}
              className="ml-auto mr-2 text-xs"
            >
              {item.badge}
            </Badge>
          )}
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>

        {/* Child items */}
        {isExpanded && (
          <div className="mt-1 space-y-0.5">
            {item.children?.map((child) => (
              <NavItem
                key={child.label}
                item={child}
                isCollapsed={isCollapsed}
                onNavigate={onNavigate}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Static item (no href, no children)
  return (
    <span
      className={cn(
        'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
        'text-muted-foreground',
        item.disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {Icon && <Icon className="h-5 w-5 shrink-0" />}
      <span className="flex-1 truncate">{item.label}</span>
    </span>
  )
}

/**
 * Section header for nav groups
 */
interface NavSectionHeaderProps {
  label: string
  isCollapsed: boolean
}

export function NavSectionHeader({ label, isCollapsed }: NavSectionHeaderProps) {
  if (isCollapsed) {
    return <div className="my-2 h-px bg-border" />
  }

  return (
    <div className="mt-4 mb-2 px-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </span>
    </div>
  )
}
